import {
  Button,
  Pill,
  RecordCard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components';
import { I } from '@/shared/icons';
import type { ChannexWebhookEventResponse } from '@/shared/api';
import { formatRelative } from '@/shared/lib/format';
import { statusLabel, statusPillVariant } from './helpers';

interface WebhookEventTableProps {
  items: ChannexWebhookEventResponse[];
  onSelect: (evt: ChannexWebhookEventResponse) => void;
}

/**
 * Desktop die Datentabelle, darunter gestapelte Karten.
 *
 * <p>Die Fläche liegt im Einstellungs-Layout und hat dort rund 714 px — die Tabelle brauchte
 * 842 px, ihre Aktionsspalte lag also auf jedem Viewport hinter einem horizontalen Scroll. Die
 * Kennung des Objekts ist gefallen: sie steht ungekürzt im Detaildialog, wo sie hingehört.</p>
 */
export function WebhookEventTable({ items, onSelect }: WebhookEventTableProps) {
  return (
    // Container-Query statt Viewport-Breakpoint — siehe `OutboxTable`.
    <div className="@container">
      <div className="@3xl:block hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Empfangen</TableHead>
              <TableHead>Meldung</TableHead>
              <TableHead>Zustand</TableHead>
              <TableHead>Fehler</TableHead>
              <TableHead className="text-right">
                <span className="sr-only">Aktion</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((evt) => (
              <TableRow key={evt.id}>
                <TableCell className="text-ink-soft whitespace-nowrap text-xs">
                  {formatRelative(evt.receivedAt)}
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs">{evt.eventType}</span>
                </TableCell>
                <TableCell>
                  <Pill variant={statusPillVariant(evt.status)} size="sm">
                    {statusLabel(evt.status)}
                  </Pill>
                </TableCell>
                <TableCell
                  className={evt.error ? 'text-sealwax max-w-xs truncate text-xs' : 'text-xs'}
                  title={evt.error ?? undefined}
                >
                  {evt.error ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    className="min-h-11"
                    onClick={() => onSelect(evt)}
                    leftIcon={<I name="Eye" size={12} aria-hidden />}
                  >
                    Einzelheiten
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="@3xl:hidden grid gap-3">
        {items.map((evt) => (
          <RecordCard
            key={evt.id}
            status={
              <Pill variant={statusPillVariant(evt.status)} size="sm">
                {statusLabel(evt.status)}
              </Pill>
            }
            identityMono
            identity={evt.eventType}
            facts={[
              { label: 'Empfangen', value: formatRelative(evt.receivedAt) },
              { label: 'Versuche', value: evt.attempts },
            ]}
            note={evt.error}
            action={
              <Button
                variant="ghost"
                className="min-h-11 w-full"
                onClick={() => onSelect(evt)}
                leftIcon={<I name="Eye" size={12} aria-hidden />}
              >
                Einzelheiten
              </Button>
            }
          />
        ))}
      </ul>
    </div>
  );
}
